import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import countries from "i18n-iso-countries";
import {
  createCenter,
  deleteCenter,
  updateCenter,
} from "../../../../redux/actions/center";
import { store } from "../../../../store";
import { getProtocols } from "../../../../services/protocolService";
import Modal from "../../../../shared/ui/modal/Modal";
import { showSuccessToast } from "../../../../shared/ui/toast/Toast";
import {
  showErrorAlert,
  showWarningAlert,
} from "../../../ui/utils/AlertUtils";

countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
const countryList = countries.getNames("en", { select: "official" });

const emptyForm = {
  centerNumber: "",
  officePhone: "",
  city: "",
  country: "",
  contactFirstName: "",
  contactLastName: "",
  contactEmail: "",
  contactPhone: "",
  status: "active",
  protocolIds: [],
};

const getContactParts = (contactName = "") => {
  const [lastName = "", ...firstNameParts] = String(contactName).split(",");

  return {
    contactFirstName: firstNameParts.join(",").trim(),
    contactLastName: lastName.trim(),
  };
};

const getInitialForm = (center) => {
  if (!center) {
    return { ...emptyForm, protocolIds: [] };
  }

  return {
    centerNumber: center.centerNumber || "",
    officePhone: center.officePhone || "",
    city: center.city || "",
    country: center.country || "",
    ...getContactParts(center.contactName),
    contactEmail: center.contactEmail || "",
    contactPhone: center.contactPhone || "",
    status: center.status || "active",
    protocolIds: (center.Protocols || center.protocols || []).map(
      (protocol) => protocol.id,
    ),
  };
};

const FormSection = ({ title, children }) => (
  <Box
    component="section"
    sx={{ bgcolor: "grey.50", borderRadius: 2, p: 2 }}>
    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
      {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

export const CenterForm = ({ visible, onClose, studyId, center = null }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(center?.id);
  const isLoading = useSelector((state) => state.center.isLoadingCenter);
  const [form, setForm] = React.useState(() => getInitialForm(center));
  const [errors, setErrors] = React.useState({});
  const [protocols, setProtocols] = React.useState([]);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] =
    React.useState(false);
  const effectiveStudyId = center?.studyId || studyId;

  React.useEffect(() => {
    if (!visible) {
      return;
    }

    setForm(getInitialForm(center));
    setErrors({});
    setDeleteConfirmationOpen(false);
  }, [center, visible]);

  React.useEffect(() => {
    if (!visible || !effectiveStudyId) {
      return undefined;
    }

    let active = true;
    setProtocols([]);

    getProtocols(effectiveStudyId)
      .then((studyProtocols) => {
        if (active) {
          setProtocols(studyProtocols || []);
        }
      })
      .catch((error) => {
        console.error("Failed to load center protocols:", error);
        if (active) {
          setProtocols([]);
        }
      });

    return () => {
      active = false;
    };
  }, [effectiveStudyId, visible]);

  const updateField = (field, value) => {
    setForm((currentForm) => ({ ...currentForm, [field]: value }));
    setErrors((currentErrors) => ({ ...currentErrors, [field]: undefined }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.centerNumber.trim()) {
      nextErrors.centerNumber = t("fieldRequired");
    }
    if (!form.city.trim()) {
      nextErrors.city = t("cityRequired");
    }
    if (!form.country) {
      nextErrors.country = t("countryRequired");
    }
    if (!form.contactFirstName.trim()) {
      nextErrors.contactFirstName = t("contactFirstNameRequired");
    }
    if (!form.contactLastName.trim()) {
      nextErrors.contactLastName = t("contactLastNameRequired");
    }
    if (!form.contactEmail.trim()) {
      nextErrors.contactEmail = t("contactEmailRequired");
    }
    if (!form.contactPhone.trim()) {
      nextErrors.contactPhone = t("contactPhoneRequired");
    }
    if (form.protocolIds.length === 0) {
      nextErrors.protocolIds = t("protocolRequired");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      centerNumber: form.centerNumber.trim(),
      officePhone: form.officePhone.trim(),
      city: form.city.trim(),
      country: form.country,
      contactName: `${form.contactLastName.trim()}, ${form.contactFirstName.trim()}`,
      contactEmail: form.contactEmail.trim(),
      contactPhone: form.contactPhone.trim(),
      status: form.status,
      studyId: effectiveStudyId,
      protocolIds: form.protocolIds,
    };
    const savedCenter = isEdit
      ? await store.dispatch(updateCenter(center.id, payload))
      : await store.dispatch(createCenter(payload));

    if (!savedCenter) {
      await showErrorAlert(
        isEdit ? t("centerUpdateError") : t("centerCreationError"),
      );
      return;
    }

    showSuccessToast(
      isEdit ? t("centerUpdatedSuccess") : t("centerCreatedSuccess"),
    );
    onClose(true);
  };

  const handleDelete = async () => {
    if (!isEdit) {
      return;
    }

    setDeleteConfirmationOpen(true);
    const confirmation = await showWarningAlert(
      t("deleteCenterConfirmation"),
      {
        confirmButtonText: t("deleteConfirmButton"),
        cancelButtonText: t("cancel"),
      },
    );

    if (!confirmation.isConfirmed) {
      setDeleteConfirmationOpen(false);
      return;
    }

    const deleted = await store.dispatch(deleteCenter(center.id));

    if (!deleted) {
      await showErrorAlert(t("centerDeleteError"));
      setDeleteConfirmationOpen(false);
      return;
    }

    showSuccessToast(t("centerDeletedSuccess"));
    onClose(true);
  };

  return (
    <Modal
      open={visible && !deleteConfirmationOpen}
      onClose={() => onClose(false)}
      closeDisabled={isLoading}
      title={isEdit ? t("updateCenter") : t("createCenter")}
      maxWidth="md"
      contentSx={{ p: 0, overflow: "hidden" }}>
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 112px)",
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minHeight: 0,
            overflowY: "auto",
            px: 3,
            pt: 1,
            pb: 3,
          }}>
          <FormSection title={t("info")}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t("centerNumber")}
                value={form.centerNumber}
                error={Boolean(errors.centerNumber)}
                helperText={errors.centerNumber}
                onChange={(event) =>
                  updateField("centerNumber", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="tel"
                label={t("officePhone")}
                value={form.officePhone}
                onChange={(event) =>
                  updateField("officePhone", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={Boolean(errors.country)}>
                <InputLabel id="center-country-label">
                  {t("country")}
                </InputLabel>
                <Select
                  labelId="center-country-label"
                  label={t("country")}
                  value={form.country}
                  onChange={(event) =>
                    updateField("country", event.target.value)
                  }>
                  {Object.entries(countryList).map(([code, name]) => (
                    <MenuItem key={code} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.country}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t("city")}
                value={form.city}
                error={Boolean(errors.city)}
                helperText={errors.city}
                onChange={(event) => updateField("city", event.target.value)}
              />
            </Grid>
          </FormSection>

          <FormSection title={t("contact")}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t("firstname")}
                value={form.contactFirstName}
                error={Boolean(errors.contactFirstName)}
                helperText={errors.contactFirstName}
                onChange={(event) =>
                  updateField("contactFirstName", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t("lastName")}
                value={form.contactLastName}
                error={Boolean(errors.contactLastName)}
                helperText={errors.contactLastName}
                onChange={(event) =>
                  updateField("contactLastName", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label={t("email")}
                value={form.contactEmail}
                error={Boolean(errors.contactEmail)}
                helperText={errors.contactEmail}
                onChange={(event) =>
                  updateField("contactEmail", event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="tel"
                label={t("phone")}
                value={form.contactPhone}
                error={Boolean(errors.contactPhone)}
                helperText={errors.contactPhone}
                onChange={(event) =>
                  updateField("contactPhone", event.target.value)
                }
              />
            </Grid>
          </FormSection>

          <FormSection title={t("statusAndProtocol")}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="center-status-label">{t("status")}</InputLabel>
                <Select
                  labelId="center-status-label"
                  label={t("status")}
                  value={form.status}
                  onChange={(event) =>
                    updateField("status", event.target.value)
                  }>
                  <MenuItem value="active">{t("active")}</MenuItem>
                  <MenuItem value="closed">{t("closed")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                required
                error={Boolean(errors.protocolIds)}>
                <InputLabel id="center-protocol-label">
                  {t("protocols")}
                </InputLabel>
                <Select
                  labelId="center-protocol-label"
                  multiple
                  label={t("protocols")}
                  value={form.protocolIds}
                  input={<OutlinedInput label={t("protocols")} />}
                  onChange={(event) =>
                    updateField("protocolIds", event.target.value)
                  }
                  renderValue={(selected) =>
                    protocols
                      .filter((protocol) => selected.includes(protocol.id))
                      .map((protocol) => protocol.reference)
                      .join(", ")
                  }>
                  {protocols.map((protocol) => (
                    <MenuItem key={protocol.id} value={protocol.id}>
                      <Checkbox
                        checked={form.protocolIds.includes(protocol.id)}
                      />
                      {protocol.reference}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.protocolIds}</FormHelperText>
              </FormControl>
            </Grid>
          </FormSection>
        </Box>

        <Divider />
        <Box sx={{ display: "flex", alignItems: "center", px: 3, py: 2 }}>
          {isEdit && (
            <Button
              type="button"
              color="error"
              variant="outlined"
              disabled={isLoading}
              onClick={handleDelete}>
              {t("delete")}
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isEdit ? t("update") : t("create")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
